import React, {useState, useTransition} from 'react';
import {Button, Form, Input, Popconfirm, Switch, Tag, message, Modal, Tooltip, Select} from 'antd';
import type {TableColumnsType} from 'antd';
import type {SearchProps} from 'antd/es/input/Search';

import {
  PlusOutlined,
  EditFilled,
  DollarOutlined,
  ReadOutlined,
  UnlockOutlined,
  LockOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import Api from 'configs';

import {useGetShops} from 'hooks/shop/useGetShop';
import {useUpdateShopActive, useToggleShopBlock, useVerifyShop} from 'hooks/shop/useUpdateShop';
import {useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';

import {TableCustom} from 'components/element/TableCustom';
import {PaginationCustom} from 'components/element/PaginationCustom';
import {
  addKeyData,
  formatNumberToString,
  formatNumberValue,
  formatDate,
} from 'utils';
import {LoadedMoney} from 'components/element/LoadedMoney';
export const Shop: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [filters, setFilters] = useState<any>({});
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [blockingShop, setBlockingShop] = useState<{shopId: string; name: string} | null>(null);
  const [blockReasonInput, setBlockReasonInput] = useState('');
  const [isSubmittingBlock, setIsSubmittingBlock] = useState(false);
  const {data: getData, isLoading, refetch} = useGetShops(filters);
  const {mutateAsync: onToggleActive} = useUpdateShopActive();
  const {mutateAsync: onToggleShopBlock} = useToggleShopBlock();
  const {mutateAsync: onVerifyShop} = useVerifyShop();
  const handleConfirm = async (shopId: string) => {
    await onToggleShopBlock(shopId);
    message.success('Đã mở khóa cửa hàng');
    await refetch();
  };
  const handleConfirmBlock = async () => {
    if (!blockingShop) return;
    try {
      setIsSubmittingBlock(true);
      await onToggleShopBlock({
        shopId: blockingShop.shopId,
        reason: blockReasonInput.trim() || undefined,
      });
      message.success(`Đã khóa cửa hàng ${blockingShop.name}`);
      setBlockingShop(null);
      setBlockReasonInput('');
      await refetch();
    } catch {
      message.error('Lỗi khi khóa cửa hàng');
    } finally {
      setIsSubmittingBlock(false);
    }
  };
  const handleIsActive = async (id: string) => {
    await onToggleActive(id);
    await refetch();
  };
  const handleCopyLink = async (id: string) => {
    const link = `https://merchant.thienloi.com/merchant/${id}`;
    try {
      await navigator.clipboard.writeText(link);
      message.success('Sao chép thành công');
    } catch (error) {
      message.error('Lỗi sao chép');
    }
  };
  const [loadingExport, setLoadingExport] = useState(false);
  const handleExportShopExcel = async () => {
    try {
      setLoadingExport(true);
      const response = await Api.get('/reports/export/shops', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'danh-sach-quan-an.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Xuất file Excel quán ăn thành công');
    } catch {
      message.error('Lỗi khi xuất file Excel');
    } finally {
      setLoadingExport(false);
    }
  };
  const columns: TableColumnsType = [
    {
      title: 'Tên Shop',
      dataIndex: 'name',
      key: 'shop',
      fixed: 'left',
      width: 220,
      render: (name: string, record: any) => (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-semibold">{name}</span>
          {record?.isCharity && (
            <Tag color="gold" className="m-0 font-medium text-xs">
              💛 Thiện nguyện
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Số điện thoại',
      key: 'numberPhone',
      width: 140,
      render: (_: any, record: any) => {
        const phone = record?.user?.numberPhone || record?.numberPhone;
        return (
          <div className="flex flex-col">
            <span className="whitespace-nowrap font-medium">
              {phone || '-'}
            </span>
            {!record?.user?._id && (
              <span className="text-[11px] text-gray-400">
                (Chưa có TK)
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Tiền trong ví',
      dataIndex: 'balance',
      key: 'balance',
      align: 'center',
      width: 150,
      sorter: true,
      render: data => {
        return (
          <div className="whitespace-nowrap font-medium">
            <p>{formatNumberValue(data)}</p>
          </div>
        );
      },
    },
    {
      title: 'Trạng thái',
      key: 'block',
      align: 'center',
      width: 160,
      render: (_: any, record: any) => {
        const isBlocked = Boolean(
          record?.isBlocked ||
          record?.typeBlock === 'ADMIN' ||
          record?.user?.isBlocked
        );
        const blockReason = record?.blockReason || record?.user?.blockReason;

        return (
          <div className="flex flex-col items-center gap-1">
            {isBlocked ? (
              <Tag color="error" className="m-0 font-medium">
                Đã khóa
              </Tag>
            ) : (
              <Tag color="success" className="m-0 font-medium">
                Hoạt động
              </Tag>
            )}

            {isBlocked && blockReason && (
              <Tooltip
                title={blockReason}
                placement="topLeft"
                overlayInnerStyle={{maxWidth: 350, wordBreak: 'break-word'}}>
                <span className="text-[11px] text-red-500 font-normal max-w-[150px] truncate cursor-pointer block">
                  Lý do: {blockReason}
                </span>
              </Tooltip>
            )}

            {isBlocked ? (
              <Popconfirm
                title="Mở khóa cửa hàng"
                description={`Bạn có chắc muốn mở khóa cho cửa hàng ${record?.name || ''}?`}
                okText="Đồng ý"
                cancelText="Hủy"
                onConfirm={() => handleConfirm(record?._id)}>
                <Button
                  size="small"
                  type="primary"
                  icon={<UnlockOutlined />}
                  className="text-xs mt-1">
                  Mở khóa
                </Button>
              </Popconfirm>
            ) : (
              <Button
                size="small"
                danger
                icon={<LockOutlined />}
                className="text-xs mt-1"
                onClick={() => {
                  setBlockingShop({
                    shopId: record?._id,
                    name: record?.name || 'Cửa hàng',
                  });
                  setBlockReasonInput('');
                }}>
                Khóa
              </Button>
            )}
          </div>
        );
      },
    },

    {
      title: 'Xác minh',
      key: 'verified',
      align: 'center',
      width: 130,
      render: (_: any, record: any) => {
        const isVerified = record?.isVerified !== undefined
          ? Boolean(record?.isVerified)
          : (record?.user?.verified ?? true);

        return (
          <Popconfirm
            title={`Xác minh cửa hàng`}
            description={`Bạn có chắc muốn xác minh cho cửa hàng ${record?.name || ''}?`}
            okText="Đồng ý"
            disabled={isVerified}
            cancelText="Hủy"
            onConfirm={async () => {
              await onVerifyShop(record?._id);
              message.success('Đã xác minh cửa hàng thành công');
              await refetch();
            }}>
            <div>
              {isVerified ? (
                <Tag color="success">Đã xác minh</Tag>
              ) : (
                <Tag className="verify cursor-pointer" color="error">
                  Chưa xác minh
                </Tag>
              )}
            </div>
          </Popconfirm>
        );
      },
    },
    {
      title: 'Cấp phép',
      dataIndex: 'isActive',
      key: 'isActive',
      align: 'center',
      width: 180,
      render: (isActive: any, data: any) => {
        return (
          <Popconfirm
            title={`Cấp phép`}
            description={`Bạn có chắc muốn ${
              !isActive ? 'cấp phép' : 'thu hồi cấp phép'
            }`}
            okText="Yes"
            cancelText="No"
            onConfirm={() => {
              if (data?.systemConfigId) {
                handleIsActive(data?._id);
              } else {
                message.error(
                  'Bảng giá chưa được chọn đề nghị cập nhật bảng giá trước khi cấp giấy phép!!!',
                );
              }
            }}>
            {isActive ? (
              <Button type="primary" icon={<UnlockOutlined />}>
                Đã cấp phép
              </Button>
            ) : (
              <Button
                icon={<LockOutlined />}
                className="!border-white bg-[#ed250e] hover:!bg-[#f25c4b] !text-white !font-medium">
                Chưa cấp phép
              </Button>
            )}
          </Popconfirm>
        );
      },
    },
    {
      title: 'Tình trạng',
      dataIndex: 'isOpen',
      align: 'center',
      key: 'isOpen',
      width: 140,
      render: (data: any, record: any) => {
        const isForceClosed =
          record?.forceClosedUntil && new Date(record.forceClosedUntil) > new Date();
        return (
          <div className="flex flex-col items-center gap-1">
            {isForceClosed ? (
              <Tooltip
                title={`Shipper báo đóng: ${record?.forceClosedReason || 'Tạm đóng hôm nay'}`}>
                <Tag color="warning" className="cursor-pointer m-0 font-medium">
                  ⚠️ Báo đóng cửa
                </Tag>
              </Tooltip>
            ) : data ? (
              <Tag color="success" className="m-0">
                Đang mở cửa
              </Tag>
            ) : (
              <Tag color="error" className="m-0">
                Đang đóng cửa
              </Tag>
            )}
            {isForceClosed && (
              <Popconfirm
                title="Mở lại quán"
                description={`Bạn có chắc muốn mở lại quán ${record?.name || ''}?`}
                okText="Mở lại"
                cancelText="Hủy"
                onConfirm={async () => {
                  try {
                    await Api.post(`/shops/${record?._id}/reopen`);
                    message.success('Đã mở lại quán thành công');
                    refetch();
                  } catch {
                    message.error('Lỗi khi mở lại quán');
                  }
                }}>
                <Button size="small" type="link" className="text-xs p-0 text-blue-600 h-auto">
                  Mở lại ngay
                </Button>
              </Popconfirm>
            )}
          </div>
        );
      },
    },
    // Q: Cột sao đánh giá
    {
      title: 'Đánh giá',
      dataIndex: 'average',
      key: 'average',
      align: 'center',
      width: 110,
      render: (data: number) => <span className="whitespace-nowrap">⭐ {data ? Number(data).toFixed(1) : '5.0'}</span>,
    },
    // M: Cột tổng đơn hoàn thành
    {
      title: 'Tổng đơn HT',
      dataIndex: 'totalCompletedOrders',
      key: 'totalCompletedOrders',
      align: 'center',
      width: 130,
      render: (data: number) => <span className="whitespace-nowrap">{data || 0} đơn</span>,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      width: 160,
      render: data => {
        return (
          <div className="whitespace-nowrap">
            <p>{formatDate(data)}</p>
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: '',
      key: 'action',
      align: 'center',
      width: 200,
      render: (text, record: any) => {
        return (
          <div className="flex flex-col justify-center gap-2 px-0 py-[4px]">
            <Button
              className="!border-white	 bg-[#2bec82] hover:!bg-[#2bec82] !text-white !font-medium"
              onClick={() => {
                form.resetFields();
                const data = {
                  ...record,
                  balance: formatNumberToString(record.balance || 0),
                };
                form.setFieldsValue(data);
                setIsOpen(true);
              }}
              icon={<DollarOutlined />}>
              Nạp tiền
            </Button>
            <Button
              icon={<ReadOutlined />}
              type="primary"
              onClick={() => {
                startTransition(() => {
                  navigate(`/shop/menu/${record?._id}`, {
                    state: {from: `/shop`},
                  });
                });
              }}>
              Chi tiết menu
            </Button>
            <Button
              onClick={() => {
                startTransition(() => {
                  navigate(`/shop/edit/${record?._id}`, {
                    state: {from: '/shop'},
                  });
                });
              }}
              icon={<EditFilled />}
              className="!border-white	 bg-[#eca52b] hover:!bg-[#edb555] !text-white !font-medium">
              Chỉnh sửa
            </Button>
            <Button
              onClick={() => {
                startTransition(() => {
                  handleCopyLink(record?._id);
                });
              }}
              icon={<CopyOutlined />}
              className="!border-white bg-[#ed250e] hover:!bg-[#f25c4b] !text-white !font-medium">
              Share Cửa Hàng
            </Button>
            {/* E: Xóa mềm quán */}
            <Popconfirm
              title="Xóa quán"
              description="Bạn có chắc muốn xóa quán này? Quán sẽ bị xóa khỏi danh sách quản lý (lịch sử đơn hàng vẫn được lưu)."
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{danger: true}}
              onConfirm={async () => {
                try {
                  await Api.delete(`/shops/soft-delete/${record._id}`);
                  message.success('Đã xóa quán thành công');
                  queryClient.invalidateQueries(['useGetShops']);
                  await refetch();
                } catch {
                  message.error('Lỗi khi xóa quán');
                }
              }}>
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                className="!border-red-500 font-medium">
                Xóa quán
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];
  const onSeachValue: SearchProps['onSearch'] = (value, _e, info) => {
    setFilters({...filters, search: value, page: 1});
  };
  const handleTableChange = (filter: any, sorter: any) => {
    if (sorter) {
      setFilters({...filters, sorter: sorter});
    } else {
      setFilters({...filters, sorter: null});
    }
  };
  return (
    <div className="container mx-auto">
      <div className="flex-col gap-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Cửa hàng</h2>
          <div className="flex gap-2 items-center">
            <Select
              className="w-44"
              placeholder="Trạng thái"
              defaultValue=""
              onChange={val => {
                setFilters({...filters, status: val || undefined, page: 1});
              }}
              options={[
                {value: '', label: 'Tất cả trạng thái'},
                {value: 'active', label: '🟢 Đang hoạt động'},
                {value: 'blocked', label: '🔴 Đã khóa'},
              ]}
            />
            <div>
              <Input.Search
                className="w-80"
                placeholder="Search theo tên hoặc số điện thoại"
                onSearch={onSeachValue}
                enterButton
              />
            </div>
            <Button
              className="!bg-green-600 hover:!bg-green-500 !text-white font-bold"
              icon={<DownloadOutlined />}
              loading={loadingExport}
              onClick={handleExportShopExcel}>
              Xuất Excel Quán
            </Button>
            <Button
              className="font-bold"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                startTransition(() => {
                  navigate('/shop/create', {
                    state: {from: '/shop'},
                  });
                });
              }}>
              Add Shop
            </Button>
          </div>
        </div>
        <div className=" flex flex-col items-end gap-3">
          <TableCustom
            isLoading={isLoading}
            columns={columns}
            data={addKeyData(getData?.result?.filter((s: any) => !s?.isDeleted))}
            onChange={handleTableChange}
            scroll={{x: 1850, y: 590}}
          />
          <PaginationCustom
            totalPage={getData?.totalPage ?? 1}
            current={getData?.page ?? 1}
            pageSize={getData?.pageSize ?? 10}
            onChange={(page, pageSize) => {
              setFilters({...filters, page, pageSize});
            }}
            onShowSizeChange={(current, size) => {
              setFilters({...filters, page: current, pageSize: size});
            }}
          />
          {isOpen ? (
            <LoadedMoney
              form={form}
              setIsOpen={setIsOpen}
              isOpen={isOpen}
              refetch={refetch}
            />
          ) : null}
        </div>
      </div>
      <Modal
        title={`Khóa cửa hàng: ${blockingShop?.name || ''}`}
        open={Boolean(blockingShop)}
        onCancel={() => setBlockingShop(null)}
        onOk={handleConfirmBlock}
        confirmLoading={isSubmittingBlock}
        okText="Xác nhận khóa"
        cancelText="Hủy"
        okButtonProps={{danger: true}}>
        <div className="py-2">
          <p className="text-sm text-gray-600 mb-2">
            Nhập lý do khóa cửa hàng (mô tả này sẽ được lưu lại để quản trị viên theo dõi):
          </p>
          <Input.TextArea
            rows={3}
            placeholder="Ví dụ: Vi phạm vệ sinh an toàn thực phẩm, không nhận đơn liên tục, đóng cửa tự ý..."
            value={blockReasonInput}
            onChange={e => setBlockReasonInput(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
};
